import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, ExternalLink, Clock } from "lucide-react";

/** Phase 1 — Recipient inbox for clause referrals.
 *  Every user can view referrals sent to them (by user_id, role, or dept).
 *  Excerpt-only — no contract number or vendor name shown. */
export default function ReferralsInbox() {
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [reply, setReply] = useState({});
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setBusy(true);
    return api.get("/referrals/mine").then(r => setRefs(r.data || []))
      .catch(() => toast.error("Failed to load referrals"))
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, []);

  const send = async (rid) => {
    const txt = (reply[rid] || "").trim();
    if (!txt) return toast.error("Response cannot be empty");
    try {
      await api.post(`/referrals/${rid}/respond`, { response: txt });
      toast.success("Response sent");
      setReply(r => ({ ...r, [rid]: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="referrals-inbox">
      <div className="flex items-center gap-3 mb-5">
        <MessageSquare className="w-5 h-5 text-[#005289]" />
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Referrals</div>
          <h1 className="text-2xl font-bold text-slate-900">Clause Referrals — Inbox</h1>
          <p className="text-sm text-slate-500 mt-1">
            Excerpts Legal has referred to you. You'll only see the section they highlighted — never the full contract.
          </p>
        </div>
      </div>

      {busy && <div className="text-sm text-slate-500 text-center py-10">Loading…</div>}
      {!busy && refs.length === 0 && (
        <div className="text-sm text-slate-500 text-center py-10 border border-dashed border-slate-300 rounded-md"
             data-testid="referrals-inbox-empty">
          No clause referrals yet.
        </div>
      )}
      <div className="space-y-3">
        {refs.map(r => (
          <div key={r.referral_id}
               className="bg-white border border-slate-200 rounded-md p-4"
               data-testid={`referral-inbox-row-${r.referral_id}`}>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${r.status === "responded" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                {r.status}
              </span>
              <span className="text-xs font-semibold text-slate-900">Asked by {r.asked_by}</span>
              <span className="text-[10px] text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" /> {new Date(r.asked_at).toLocaleString()}
              </span>
              {r.responses_count > 0 && (
                <span className="ml-auto text-[10px] text-slate-500">
                  {r.responses_count} response{r.responses_count === 1 ? "" : "s"}
                </span>
              )}
            </div>
            <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">Question</div>
            <div className="text-sm text-slate-800 mb-2">{r.question}</div>
            <div className="text-[11px] font-bold text-slate-500 uppercase mb-1">Excerpt</div>
            <pre className="text-xs text-slate-700 bg-slate-50 border border-slate-100 rounded p-3 whitespace-pre-wrap font-sans max-h-40 overflow-auto">{r.excerpt}</pre>

            {r.my_responses && r.my_responses.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="text-[11px] font-bold text-slate-500 uppercase">Prior responses</div>
                {r.my_responses.map((rr, i) => (
                  <div key={i} className="text-xs text-slate-700 bg-emerald-50 border border-emerald-100 rounded p-2">
                    {rr.response}
                    <div className="text-[10px] text-slate-500 mt-1">{new Date(rr.at).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            )}

            {openId === r.referral_id ? (
              <div className="mt-3 space-y-2">
                <textarea value={reply[r.referral_id] || ""} onChange={e => setReply(s => ({ ...s, [r.referral_id]: e.target.value }))}
                          data-testid={`referral-inbox-reply-${r.referral_id}`}
                          rows={3} maxLength={8000}
                          placeholder="Type your response…"
                          className="w-full p-2 text-sm border border-slate-200 rounded resize-none focus:border-[#005289] focus:outline-none" />
                <div className="flex items-center gap-2">
                  <button onClick={() => setOpenId(null)} className="h-8 px-3 text-xs border border-slate-200 rounded">Cancel</button>
                  <button onClick={() => send(r.referral_id)}
                          data-testid={`referral-inbox-send-${r.referral_id}`}
                          className="ml-auto h-8 px-3 text-xs font-semibold bg-[#005289] text-white rounded flex items-center gap-1">
                    <Send className="w-3 h-3" /> Send response
                  </button>
                </div>
              </div>
            ) : (
              <button onClick={() => setOpenId(r.referral_id)}
                      data-testid={`referral-inbox-open-${r.referral_id}`}
                      className="mt-3 h-8 px-3 text-xs border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Respond
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
